import {
    Mesh,
    DoubleSide,
    MeshBasicMaterial,
    Vector3,
    PlaneGeometry,
    SphereGeometry,
    Sprite,
    SpriteMaterial,
} from 'three';

import { VRObject3D } from './VRObject3D';
import { cornerHandleLocalFromVertex, vertexLocalFromCornerHandle } from './helpers/worldHelpers';
import { emptyTexture, INDICATOR_HIT_SIZE, myLocationTexture, dragTexture } from './helpers/texturesAndGeometries';


class VREmbeddedWebpageObject extends VRObject3D {

    constructor(containerName, camera, texture, x, y, z, model) {

        super(containerName);

        this.camera = camera;
        this.material = new MeshBasicMaterial({ map: texture, side: DoubleSide, transparent: true });

        this.mesh = new Mesh(new PlaneGeometry(48, 48), this.material);
        this.mesh.type = 'embeddedWebpage';
        this.mesh.name = 'embeddedWebpage';
        this.name = 'embeddedWebpage';
        this.type = 'embeddedWebpage';

        this.model = model;

        this.objConfig.dragOnlyWhenSelected = false;
        this.objConfig.isResizable = false;
        this.objConfig.cubeSize = 20;
        this.objConfig.offset = {
            topLeft: { x: 2, y: -3.5 },
            topRight: { x: -2, y: -3.5 },
            bottomRight: { x: -2, y: 3.5 },
            bottomLeft: { x: 2, y: 3.5 },
        };

        this.perspectivePosition = {
            topLeft: new Vector3(-24, 24, 0),
            topRight: new Vector3(24, 24, 0),
            bottomRight: new Vector3(24, -24, 0),
            bottomLeft: new Vector3(-24, -24, 0),
        };
        this.originalPerspectivePosition = {
            topLeft: this.perspectivePosition.topLeft.clone(),
            topRight: this.perspectivePosition.topRight.clone(),
            bottomRight: this.perspectivePosition.bottomRight.clone(),
            bottomLeft: this.perspectivePosition.bottomLeft.clone(),
        };

        this.dragCubeCorners = {};
        this.dragSprites = {};
        this.centerHandle = null;
        this.centerHandleSprite = null;

        // INIT
        this.camera !== null ? this._setInitialPosRot() : this.setPosition(x, y, z);

        this.add(this.mesh);

        this.mesh.groupDetails = { title: $trans('embedded-webpage'), icon: 'info', editTitle: $trans('edit-embedded-webpage'), dataLang: 'edit-embedded-webpage' }
        this.defaultTexture = texture;
        this.useIcon = true;
        this.customIcon = false;

        this.editControls();
        this.applyPerspectiveDragAndDrop();
        this.updateDragBehavior(true);

        $("#embeddedWebpageModal .modal-background").on("click", () => {
            this.closeModal();
        })
        $("#embeddedWebpageModal .close-modal").on("click", () => {
            this.closeModal();
        })
        //result
        return this;
    }

    showEditModal = () => {
        $("#embedded-webpage-modal .modal-header-title").text($trans('edit-embedded-webpage'));
        $("#embedded-webpage-modal .modal-header-title").attr('data-lang', 'edit-embedded-webpage');
        $("#embedded-webpage-modal").addClass("edit");

        const { iframe, image } = this.dbConfig;
        const hasImage = !!(image?.url);
        const useIcon = this.dbConfig.useIcon !== undefined ? this.dbConfig.useIcon === true : !hasImage;

        $("#embedded_webpage_image").attr('src', image?.url || 'textures/empty.png');
        $("#embedded-webpage-iframe-url").val(iframe);
        $("#embedded_webpage_image_aspect_ratio").val(image?.aspectRatio);
        $("#use_embedded_webpage_icon").prop('checked', useIcon);

        $("#upload_embedded_webpage_image .upload-watermark").toggleClass("hide", hasImage);
        $("#removeEmbeddedWebpageImage").toggleClass("active", hasImage);

        $("#embedded-webpage-modal").addClass("is-active");
    }

    openModal = () => {
        const { iframe } = this.dbConfig;
        $("#embedded-webpage-content").html(`<iframe src="${iframe}" allow="camera; microphone"></iframe>`);
        $("#embeddedWebpageModal").addClass("is-active");
    }
    closeModal = () => {
        $("#embedded-webpage-content").html(`<iframe src="${''}"></iframe>`);
        $("#embeddedWebpageModal").removeClass("is-active");
    }

    isDefaultEmbeddedWebpageImage = (url) => {
        return !url || String(url).includes('/empty.png') || String(url).endsWith('empty.png');
    }

    getCustomImageUrl = () => {
        const url = this.dbConfig?.image?.url;
        return this.isDefaultEmbeddedWebpageImage(url) ? null : url;
    }

    isIndicatorMode = () => {
        return !!this.useIcon || !this.customIcon;
    }

    getMeshSize = () => {
        const params = this.mesh?.geometry?.parameters;
        return {
            width: params?.width || 48,
            height: params?.height || 48,
        };
    }

    applyIndicatorHitGeometry = () => {
        this.updateMeshGeometry(INDICATOR_HIT_SIZE, INDICATOR_HIT_SIZE);
        const half = INDICATOR_HIT_SIZE / 2;
        this.perspectivePosition = {
            topLeft: new Vector3(-half, half, 0),
            topRight: new Vector3(half, half, 0),
            bottomRight: new Vector3(half, -half, 0),
            bottomLeft: new Vector3(-half, -half, 0),
        };
        this.applyPerspectiveDragAndDrop();
    }

    restorePerspectiveFromDbConfig = () => {
        ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'].forEach((name) => {
            const prop = this.dbConfig?.[name];
            if (prop) {
                this.perspectivePosition[name] = new Vector3(prop.x, prop.y, prop.z || 0);
            }
            const orig = this.dbConfig?.original?.[name];
            if (orig) {
                this.originalPerspectivePosition[name] = new Vector3(orig.x, orig.y, orig.z || 0);
            }
        });
    }

    hasSavedPerspectivePositions = () => {
        const corners = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'];
        return corners.every((name) => {
            const p = this.dbConfig?.[name];
            return p && Number.isFinite(p.x) && Number.isFinite(p.y);
        });
    }

    resetPerspectivePositions = (width, height) => {
        const halfW = width / 2;
        const halfH = height / 2;
        const corners = {
            topLeft: new Vector3(-halfW, halfH, 0),
            topRight: new Vector3(halfW, halfH, 0),
            bottomRight: new Vector3(halfW, -halfH, 0),
            bottomLeft: new Vector3(-halfW, -halfH, 0),
        };

        if (!this.dbConfig.original) this.dbConfig.original = {};
        Object.entries(corners).forEach(([name, vector]) => {
            this.perspectivePosition[name] = vector.clone();
            this.dbConfig[name] = vector.clone();
            this.dbConfig.original[name] = vector.clone();
            this.originalPerspectivePosition[name] = vector.clone();
        });
    }

    resetPerspective = () => {
        this.removeDragPoints();

        if (this.useIcon) {
            this.applyIndicatorHitGeometry();
            return;
        }

        const { width, height } = this.getMeshSize();
        this.updateMeshGeometry(width, height);
        this.resetPerspectivePositions(width, height);
        this.applyPerspectiveDragAndDrop();
        if (this.model?.editMode) {
            this.addDragPoints();
            if (this.isSelected && this.dbConfig?.showDragCorners !== false) {
                this.showDragHandles();
            }
        }
    }

    setUseIcon = (useIcon) => {
        const next = !!useIcon;
        if (this.useIcon === next) {
            this.customIcon = !next;
            this.objConfig.isResizable = !next;
            return;
        }

        this.useIcon = next;
        this.customIcon = !next;
        this.objConfig.isResizable = !next;
        if (this.dbConfig) this.dbConfig.useIcon = this.useIcon;

        if (this.useIcon) {
            this.updateDragBehavior();
            this.updateMeshTexture();
            return;
        }

        this.restorePerspectiveFromDbConfig();
        this.updateDragBehavior();
        this.updateMeshTexture(null, !this.hasSavedPerspectivePositions());
    }

    updateDragBehavior = (isInitializing = false) => {
        if (this.useIcon) {
            this.objConfig.dragOnlyWhenSelected = false;
            this.objConfig.isResizable = false;
            if (this.model?.editMode) {
                if (!isInitializing) {
                    this.applyIndicatorHitGeometry();
                    this.removeDragPoints();
                }
                if (this.isSelected) {
                    this.hideDragHandles();
                }
            }
            return;
        }

        this.objConfig.dragOnlyWhenSelected = true;
        this.objConfig.isResizable = true;
        if (this.model?.editMode) {
            if (!isInitializing) {
                this.addDragPoints();
            }
            if (this.isSelected) {
                this.showDragHandles();
            }
        }
    }

    updateMeshTexture = (imageLoadCallback = null, resetPerspective = false) => {
        if (this.useIcon) {
            if (this.model?.editMode) {
                this.mesh.material.map = emptyTexture;
                this.applyIndicatorHitGeometry();
            } else {
                this.mesh.material.map = this.defaultTexture;
            }
            this.mesh.material.needsUpdate = true;
            imageLoadCallback && imageLoadCallback();
            return;
        }

        const url = this.getCustomImageUrl();
        const aspectRatio = this.dbConfig?.image?.aspectRatio || 1;
        if (url) {
            this.setGeometry(url, aspectRatio, true);
            if (resetPerspective) {
                const { width, height } = this.getMeshSize();
                this.resetPerspectivePositions(width, height);
            }
            this.applyPerspectiveDragAndDrop();
            if (this.model?.editMode) {
                this.addDragPoints();
                if (this.isSelected) this.showDragHandles();
            }
            imageLoadCallback && imageLoadCallback();
            return;
        }

        if (this.model?.editMode) {
            this.mesh.material.map = emptyTexture;
        } else {
            this.mesh.material.map = this.defaultTexture;
        }
        this.mesh.material.needsUpdate = true;
        if (resetPerspective) {
            const { width, height } = this.getMeshSize();
            this.resetPerspectivePositions(width, height);
        }
        this.applyPerspectiveDragAndDrop();
        if (this.model?.editMode) {
            this.addDragPoints();
            if (this.isSelected) this.showDragHandles();
        }
        imageLoadCallback && imageLoadCallback();
    }

    setDbConfig(config) {
        super.setDbConfig(config);

        ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'].forEach((name) => {
            const prop = this.dbConfig[name];
            if (prop && (prop.x !== undefined && prop.y !== undefined)) {
                this.perspectivePosition[name] = new Vector3(prop.x, prop.y, prop.z || 0);
            }
        });

        if (config.useIcon !== undefined) {
            this.useIcon = config.useIcon === true;
        } else if (config.image !== undefined) {
            this.useIcon = !this.getCustomImageUrl();
        }
        this.customIcon = this.useIcon === false;
        this.objConfig.isResizable = !this.useIcon;
        this.dbConfig.useIcon = this.useIcon;

        this.updateDragBehavior();
        this.updateMeshTexture(null, !this.hasSavedPerspectivePositions());
    }

    onSelect() {
        if (this.useIcon || !this.customIcon) {
            super.onSelect();
            return;
        }
        if (!this.dragCubeCorners["topLeft"]) {
            this.addDragPoints();
        }
        this.showDragHandles();
        super.onSelect();
    }

    onDeselect() {
        this.hideDragHandles();
        super.onDeselect();
    }

    showDragHandles() {
        if (this.useIcon) return;
        Object.values(this.dragSprites).forEach(sprite => {
            if (sprite) sprite.visible = true;
        });
        Object.values(this.dragCubeCorners).forEach(cube => {
            if (cube) cube.visible = true;
        });
        if (this.centerHandle) this.centerHandle.visible = true;
        if (this.centerHandleSprite) this.centerHandleSprite.visible = true;
    }

    hideDragHandles() {
        Object.values(this.dragSprites).forEach(sprite => {
            if (sprite) sprite.visible = false;
        });
        Object.values(this.dragCubeCorners).forEach(cube => {
            if (cube) cube.visible = false;
        });
        if (this.centerHandle) this.centerHandle.visible = false;
        if (this.centerHandleSprite) this.centerHandleSprite.visible = false;
    }

    syncHandleVisuals = () => {
        const sx = this.mesh?.scale?.x || 1;
        const sy = this.mesh?.scale?.y || 1;
        const sz = this.mesh?.scale?.z || 1;
        const invScale = new Vector3(
            sx !== 0 ? 1 / sx : 1,
            sy !== 0 ? 1 / sy : 1,
            sz !== 0 ? 1 / sz : 1
        );

        Object.values(this.dragCubeCorners).forEach((cube) => {
            if (!cube) return;
            cube.scale.copy(invScale);
            cube.renderOrder = 10000;
        });

        if (this.centerHandle) {
            this.centerHandle.scale.copy(invScale);
            this.centerHandle.renderOrder = 10000;
        }
    }

    addDragPoints() {
        if (this.useIcon) return;
        if (this.dragCubeCorners["topLeft"]) return;

        const sphereRadius = this.objConfig.cubeSize / 2;
        const hitRadius = sphereRadius * (this.objConfig.cornerHitAreaScale || 1);
        const cubeGeometry = new SphereGeometry(hitRadius, 8, 8);
        const cubeMaterial = new MeshBasicMaterial({
            color: 0x6B9D11,
            opacity: 0,
            transparent: true,
            depthWrite: false,
            depthTest: false,
            renderOrder: 999,
        });

        const positionAttribute = this.mesh.geometry.attributes.position;
        if (!positionAttribute || positionAttribute.count < 4) return;

        const cornerNames = ['topLeft', 'topRight', 'bottomRight', 'bottomLeft'];
        const cornerIndices = [0, 1, 3, 2];

        this.dragCubeCorners = {};

        cornerIndices.forEach((index, i) => {
            const offset = this.objConfig.offset[cornerNames[i]];
            const x = positionAttribute.getX(index) + (offset?.x || 0);
            const y = positionAttribute.getY(index) + (offset?.y || 0);
            const z = positionAttribute.getZ(index);

            const cube = new Mesh(cubeGeometry, cubeMaterial);
            cube.name = cornerNames[i];
            cube.position.set(x, y + this.objConfig.cubeSize / 2, z + 1);
            cube.renderOrder = 10000;

            const spriteRadius = this.objConfig.cubeSize / 2 * 1.1;
            const spriteGeometry = new SphereGeometry(spriteRadius, 16, 16);
            const spriteMaterial = new MeshBasicMaterial({
                color: 0x6B9D11,
                transparent: true,
                opacity: 0.5,
                depthTest: false,
                depthWrite: false
            });
            const sprite = new Mesh(spriteGeometry, spriteMaterial);
            sprite.name = `sprite_${cornerNames[i]}`;
            sprite.position.set(0, -this.objConfig.cubeSize / 2, 0);
            sprite.raycast = () => { };
            sprite.renderOrder = 10001;

            const spriteIconMaterial = new SpriteMaterial({
                map: myLocationTexture,
                depthTest: false,
                transparent: true
            });
            const spriteIcon = new Sprite(spriteIconMaterial);
            spriteIcon.name = `spriteIcon_${cornerNames[i]}`;
            spriteIcon.position.set(0, -this.objConfig.cubeSize / 2, 0.1);
            spriteIcon.scale.set(this.objConfig.cubeSize / 2, this.objConfig.cubeSize / 2, this.objConfig.cubeSize / 2);
            spriteIcon.raycast = () => { };
            spriteIcon.renderOrder = 10002;

            cube.add(sprite);
            cube.add(spriteIcon);

            this.dragSprites[sprite.name] = sprite;
            this.mesh.add(cube);
            this.dragCubeCorners[cube.name] = cube;
        });

        const centerGeometry = new SphereGeometry(hitRadius, 8, 8);
        const centerMaterial = new MeshBasicMaterial({
            color: 0x6B9D11,
            opacity: 0,
            transparent: true
        });

        this.centerHandle = new Mesh(centerGeometry, centerMaterial);
        this.centerHandle.name = 'centerHandle';
        this.centerHandle.position.set(0, 0, 1);
        this.centerHandle.raycast = () => { };
        this.centerHandle.renderOrder = 10000;

        const centerSpriteRadius = sphereRadius * 1.1;
        const centerSpriteGeometry = new SphereGeometry(centerSpriteRadius, 16, 16);
        const centerSpriteMaterial = new MeshBasicMaterial({
            color: 0x6B9D11,
            transparent: true,
            opacity: 0.5,
            depthTest: false,
            depthWrite: false
        });
        this.centerHandleSprite = new Mesh(centerSpriteGeometry, centerSpriteMaterial);
        this.centerHandleSprite.name = 'sprite_centerHandle';
        this.centerHandleSprite.position.set(0, 0, 0);
        this.centerHandleSprite.raycast = () => { };
        this.centerHandleSprite.renderOrder = 10001;

        const centerSpriteIconMaterial = new SpriteMaterial({
            map: dragTexture,
            depthTest: false,
            transparent: true
        });
        this.centerHandleSpriteIcon = new Sprite(centerSpriteIconMaterial);
        this.centerHandleSpriteIcon.name = 'spriteIcon_centerHandle';
        this.centerHandleSpriteIcon.position.set(0, 0, 0.1);
        this.centerHandleSpriteIcon.scale.set(sphereRadius, sphereRadius, sphereRadius);
        this.centerHandleSpriteIcon.raycast = () => { };
        this.centerHandleSpriteIcon.renderOrder = 10002;

        this.centerHandle.add(this.centerHandleSprite);
        this.centerHandle.add(this.centerHandleSpriteIcon);
        this.mesh.add(this.centerHandle);
        this.syncHandleVisuals();

        this.hideDragHandles();
    }

    removeDragPoints() {
        Object.entries(this.dragSprites).forEach(([name]) => {
            if (this.dragSprites[name]) {
                this.dragSprites[name].parent?.remove(this.dragSprites[name]);
                this.dragSprites[name] = null;
            }
        });
        Object.entries(this.dragCubeCorners).forEach(([name]) => {
            this.mesh.remove(this.dragCubeCorners[name]);
            this.dragCubeCorners[name] = null;
        });
        if (this.centerHandle) {
            this.mesh.remove(this.centerHandle);
            this.centerHandle = null;
        }
        if (this.centerHandleSprite) this.centerHandleSprite = null;
        if (this.centerHandleSpriteIcon) this.centerHandleSpriteIcon = null;
    }

    updateLivePosition(name, position) {
        this.dbConfig[name] = this.perspectivePosition[name].clone();
        this.perspectivePosition[name].copy(position);
        this.applyPerspectiveDragAndDrop();
    }

    updateMeshCorner(cornerName, newPosition) {
        const positionAttribute = this.mesh.geometry.attributes.position;
        const cornerMappings = {
            topLeft: 0,
            topRight: 1,
            bottomRight: 3,
            bottomLeft: 2
        };

        const vertex = vertexLocalFromCornerHandle(newPosition, this.objConfig, cornerName);
        const vertexPosition = new Vector3(vertex.x, vertex.y, vertex.z);

        this.perspectivePosition[cornerName] = vertexPosition;
        this.dbConfig[cornerName] = vertexPosition.clone();

        const vertexIndex = cornerMappings[cornerName];
        if (vertexIndex !== undefined) {
            positionAttribute.setXYZ(
                vertexIndex,
                vertexPosition.x,
                vertexPosition.y,
                vertexPosition.z
            );
            positionAttribute.needsUpdate = true;
            this.mesh.geometry.computeBoundingBox();
            this.mesh.geometry.computeBoundingSphere();
        }
    }

    applyPerspectiveDragAndDrop() {
        const points = this.perspectivePosition;
        const positionAttribute = this.mesh.geometry.attributes.position;
        const uvAttribute = this.mesh.geometry.attributes.uv;

        const cornerMappings = {
            topLeft: 0,
            topRight: 1,
            bottomRight: 3,
            bottomLeft: 2
        };

        Object.entries(cornerMappings).forEach(([corner, index]) => {
            if (points[corner]) {
                positionAttribute.setXY(index, points[corner].x, points[corner].y);
                if (this.dragCubeCorners && this.dragCubeCorners[corner]) {
                    const handle = cornerHandleLocalFromVertex(points[corner], this.objConfig, corner);
                    this.dragCubeCorners[corner].position.set(handle.x, handle.y, handle.z);
                }
            }
        });

        uvAttribute.setXY(cornerMappings['topLeft'], 0, 1);
        uvAttribute.setXY(cornerMappings['topRight'], 1, 1);
        uvAttribute.setXY(cornerMappings['bottomRight'], 1, 0);
        uvAttribute.setXY(cornerMappings['bottomLeft'], 0, 0);

        positionAttribute.needsUpdate = true;
        uvAttribute.needsUpdate = true;
        this.syncHandleVisuals();

        this.mesh.geometry.computeBoundingBox();
        this.mesh.geometry.computeBoundingSphere();
    }
}

export { VREmbeddedWebpageObject };
